/**
 * IntegrationScenarios — 3 scenari pubblici di integrazione
 * Stile: diagrammi SVG minimal monocromatici (blu primary), no infografiche.
 * Pubblico: panoramica visiva. Versione tecnica completa nel pack inviato all'IT.
 */
import { Card } from '@/components/ui/card';

const scenarios = [
  {
    id: 'embedded',
    label: 'Scenario A',
    title: 'Embedded Widget',
    coverage: 'Caso più comune (~70%)',
    description:
      "Refertius vive dentro la cartella clinica come pannello laterale o tab dedicato. Il medico avvia la visita senza mai uscire dal proprio gestionale; il referto firmato torna automaticamente nella cartella del paziente.",
    steps: [
      'Il medico apre il paziente nel gestionale (Dedalus, GPI, software custom)',
      "Clicca il tab 'Refertius' integrato e registra la visita",
      'Il referto firmato viene salvato in automatico nella cartella clinica',
    ],
    Diagram: EmbeddedDiagram,
  },
  {
    id: 'deeplink',
    label: 'Scenario B',
    title: 'Deep Link con SSO',
    coverage: 'Cliniche che non modificano il proprio frontend',
    description:
      "Un bottone 'Apri Refertius' nel gestionale fa Single Sign-On verso Refertius. Il medico completa la visita in una nuova finestra; il referto rientra nel gestionale via webhook.",
    steps: [
      "Dal gestionale il medico clicca 'Apri Refertius' (SSO automatico)",
      'Registra e firma il referto nell\'interfaccia Refertius',
      'Webhook restituisce il referto strutturato al gestionale',
    ],
    Diagram: DeepLinkDiagram,
  },
];

export function IntegrationScenarios() {
  return (
    <section className="py-12 sm:py-16 px-4 border-t border-border">
      <div className="container mx-auto max-w-5xl">
        <header className="text-center mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-primary mb-3">
            Come si integra Refertius
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            Due scenari di integrazione coprono il 100% delle strutture sanitarie italiane.
            In entrambi la Verifica Pre-Firma e la finalizzazione del referto restano dentro Refertius: il referto esce
            sempre come PDF finalizzato dal medico, mai come testo grezzo modificabile.
            La documentazione tecnica completa per lo scenario scelto è inclusa nell'Integration Pack inviato all'IT,
            insieme a una <strong className="text-foreground">Sandbox API gratuita</strong> per testare l'integrazione senza toccare i dati di produzione.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {scenarios.map(({ id, label, title, coverage, description, steps, Diagram }) => (
            <Card key={id} className="p-6 flex flex-col">
              <div className="text-xs font-medium text-muted-foreground tracking-wider uppercase mb-2">
                {label}
              </div>
              <h3 className="font-serif text-xl text-primary mb-1">{title}</h3>
              <p className="text-xs text-muted-foreground mb-5">{coverage}</p>

              <div className="bg-muted/30 rounded-md p-4 mb-5 border border-border">
                <Diagram />
              </div>

              <p className="text-sm text-foreground-soft mb-4 leading-relaxed">{description}</p>

              <div className="mt-auto">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Cosa vede il medico
                </p>
                <ol className="space-y-2">
                  {steps.map((step, i) => (
                    <li key={i} className="text-sm text-foreground-soft flex gap-2">
                      <span className="font-medium text-primary shrink-0">{i + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </Card>
          ))}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-10 max-w-2xl mx-auto">
          Lo scenario che ti serve è uno di questi? Richiedi l'Integration Pack: l'IT della tua struttura
          riceve due chiavi API (Sandbox per i test, Live per la produzione), endpoint REST v1 e FHIR R4,
          snippet pronti all'uso e guida tecnica completa per lo scenario specifico.
        </p>
      </div>
    </section>
  );
}

/* ------------------------- Diagrammi SVG minimal ------------------------- */

function EmbeddedDiagram() {
  return (
    <svg viewBox="0 0 240 120" className="w-full h-auto" role="img" aria-label="Diagramma Embedded Widget">
      {/* Gestionale frame */}
      <rect x="10" y="15" width="220" height="90" rx="4" fill="none" stroke="hsl(var(--border))" strokeWidth="1.5" />
      <text x="20" y="30" fontSize="8" fill="hsl(var(--muted-foreground))" fontFamily="system-ui">Gestionale</text>
      <line x1="10" y1="36" x2="230" y2="36" stroke="hsl(var(--border))" strokeWidth="1" />
      {/* Cartella paziente */}
      <rect x="20" y="44" width="130" height="52" rx="2" fill="none" stroke="hsl(var(--muted-foreground) / 0.4)" strokeWidth="1" />
      <text x="26" y="56" fontSize="7" fill="hsl(var(--muted-foreground))" fontFamily="system-ui">Cartella paziente</text>
      <line x1="26" y1="64" x2="140" y2="64" stroke="hsl(var(--muted-foreground) / 0.3)" strokeWidth="0.5" />
      <line x1="26" y1="72" x2="140" y2="72" stroke="hsl(var(--muted-foreground) / 0.3)" strokeWidth="0.5" />
      <line x1="26" y1="80" x2="120" y2="80" stroke="hsl(var(--muted-foreground) / 0.3)" strokeWidth="0.5" />
      <line x1="26" y1="88" x2="135" y2="88" stroke="hsl(var(--muted-foreground) / 0.3)" strokeWidth="0.5" />
      {/* Refertius widget embedded */}
      <rect x="158" y="44" width="62" height="52" rx="2" fill="hsl(var(--primary) / 0.08)" stroke="hsl(var(--primary))" strokeWidth="1.5" />
      <text x="164" y="56" fontSize="7" fill="hsl(var(--primary))" fontFamily="system-ui" fontWeight="600">Refertius</text>
      <circle cx="189" cy="74" r="8" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" />
      <circle cx="189" cy="74" r="3" fill="hsl(var(--primary))" />
      <text x="170" y="92" fontSize="6" fill="hsl(var(--primary))" fontFamily="system-ui">Registra</text>
    </svg>
  );
}

function DeepLinkDiagram() {
  return (
    <svg viewBox="0 0 240 120" className="w-full h-auto" role="img" aria-label="Diagramma Deep Link SSO">
      {/* Gestionale */}
      <rect x="10" y="20" width="90" height="80" rx="4" fill="none" stroke="hsl(var(--border))" strokeWidth="1.5" />
      <text x="18" y="34" fontSize="8" fill="hsl(var(--muted-foreground))" fontFamily="system-ui">Gestionale</text>
      <line x1="10" y1="40" x2="100" y2="40" stroke="hsl(var(--border))" strokeWidth="1" />
      <rect x="22" y="58" width="66" height="22" rx="3" fill="hsl(var(--primary) / 0.08)" stroke="hsl(var(--primary))" strokeWidth="1.2" />
      <text x="29" y="72" fontSize="7" fill="hsl(var(--primary))" fontFamily="system-ui" fontWeight="600">Apri Refertius</text>
      {/* Freccia SSO */}
      <line x1="105" y1="55" x2="135" y2="55" stroke="hsl(var(--primary))" strokeWidth="1.5" markerEnd="url(#arrowB)" />
      <text x="108" y="51" fontSize="6" fill="hsl(var(--primary))" fontFamily="system-ui">SSO</text>
      {/* Refertius */}
      <rect x="140" y="20" width="90" height="80" rx="4" fill="hsl(var(--primary) / 0.05)" stroke="hsl(var(--primary))" strokeWidth="1.5" />
      <text x="148" y="34" fontSize="8" fill="hsl(var(--primary))" fontFamily="system-ui" fontWeight="600">Refertius</text>
      <line x1="140" y1="40" x2="230" y2="40" stroke="hsl(var(--primary))" strokeWidth="1" />
      <circle cx="185" cy="62" r="8" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" />
      <circle cx="185" cy="62" r="3" fill="hsl(var(--primary))" />
      <text x="162" y="84" fontSize="6" fill="hsl(var(--primary))" fontFamily="system-ui">Visita + Firma</text>
      {/* Webhook return */}
      <line x1="135" y1="78" x2="105" y2="78" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeDasharray="3,2" markerEnd="url(#arrowB)" />
      <text x="106" y="91" fontSize="6" fill="hsl(var(--primary))" fontFamily="system-ui">Webhook</text>
      <defs>
        <marker id="arrowB" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="hsl(var(--primary))" />
        </marker>
      </defs>
    </svg>
  );
}
